<template>
  <div class="card">
    <div class="card-body">
      <form @submit.prevent="submitForm">
        <div class="mb-3">
          <label for="teamName" class="form-label d-flex align-items-center"
            >Bereichsname <span class="badge bg-danger ms-2">Pflichtfeld</span></label
          >
          <input
            type="text"
            class="form-control"
            id="teamName"
            v-model="form.name"
            :class="{ 'is-invalid': errors.name }"
            required
          />
          <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
        </div>

        <div class="mb-3">
          <label for="teamDescription" class="form-label">Beschreibung</label>
          <textarea
            class="form-control"
            id="teamDescription"
            v-model="form.description"
            rows="3"
          ></textarea>
          <div class="form-text">Beschreibe deinen Bereich kurz.</div>
        </div>

        <div class="mb-4">
          <h5>Quittungsnummer-Format</h5>
          <p class="text-muted small">
            Definiere das Format für die Quittungsnummern in diesem Bereich.
          </p>

          <div class="row mb-3">
            <div class="col-md-6">
              <label for="receiptPrefix" class="form-label">Präfix (optional)</label>
              <input
                type="text"
                class="form-control"
                id="receiptPrefix"
                v-model="form.receiptNumberPrefix"
                maxlength="3"
                                 placeholder="z.B. ABC"
                 style="text-transform: uppercase"
                 @input="form.receiptNumberPrefix = $event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')"
              />
              <div class="form-text">Maximal 3 Großbuchstaben oder Zahlen</div>
            </div>
            <div class="col-md-6">
              <label for="receiptFormat" class="form-label">Nummerierungsformat</label>
                              <select
                  class="form-select"
                  id="receiptFormat"
                  v-model="form.receiptNumberFormat"
                >
                  <option value="sequential">Fortlaufende Nummern (z.B. -1, -2, -3...)</option>
                  <option value="year_sequential">Jahr + fortlaufend (z.B. 2025-1, jedes Jahr bei 1 startend)</option>
                  <option value="year_month_sequential">Jahr + Monat + fortlaufend (z.B. 202501-1, jeden Monat bei 1 startend)</option>
                  <option value="year_month_day_sequential">Jahr + Monat + Tag + fortlaufend (z.B. 20250101-1, jeden Tag bei 1 startend)</option>
                </select>
            </div>
          </div>

          <div class="alert alert-info">
            <small>
              <strong>Beispiel:</strong> 
              <span v-if="form.receiptNumberPrefix">{{ form.receiptNumberPrefix }}</span><span v-if="getExampleNumber()">{{ getExampleNumber() }}</span><span v-if="!form.receiptNumberPrefix && !getExampleNumber()">0001</span>
            </small>
          </div>
        </div>

  
        <div class="d-flex justify-content-end">
          <button type="button" class="btn btn-secondary me-2" @click="handleCancel">
            Abbrechen
          </button>
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Speichern
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { teamService } from '@/api/teams'
import { showSuccess, showConfirmDialogWithDeny } from '@/services/notification'


const props = defineProps({
  team: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['success', 'error', 'cancel', 'edit-schema'])


const originalData = ref(null)
const hasUnsavedChanges = ref(false)

const form = reactive({
  name: '',
  description: '',
  receiptNumberPrefix: '',
  receiptNumberFormat: 'sequential',
  fields: [],
})

const errors = reactive({
  name: '',
})


const initForm = () => {
  if (!props.team) return

  form.name = props.team.name || ''
  form.description = props.team.description || ''
  form.receiptNumberPrefix = props.team.receiptNumberPrefix || ''
  form.receiptNumberFormat = props.team.receiptNumberFormat || 'sequential'
  form.fields = props.team.fields ? [...props.team.fields] : []

  
  originalData.value = {
    name: props.team.name || '',
    description: props.team.description || '',
    receiptNumberPrefix: props.team.receiptNumberPrefix || '',
    receiptNumberFormat: props.team.receiptNumberFormat || 'sequential',
  }
  
  hasUnsavedChanges.value = false
}

const editSchema = () => {
  emit('edit-schema')
}

const getFieldTypeLabel = (type) => {
  const typeLabels = {
    text: 'Text',
    email: 'E-Mail',
    number: 'Zahl',
    date: 'Datum',
    textarea: 'Textbereich',
    checkbox: 'Checkbox',
    select: 'Auswahl'
  }
  return typeLabels[type] || type
}
const getExampleNumber = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  switch (form.receiptNumberFormat) {
    case 'sequential':
      return `${form.receiptNumberPrefix ? '-' : ''}1`
    case 'year_sequential':
      return `${form.receiptNumberPrefix ? '-' : ''}${year}-1`
    case 'year_month_sequential':
      return `${form.receiptNumberPrefix ? '-' : ''}${year}${month}-1`
    case 'year_month_day_sequential':
      return `${form.receiptNumberPrefix ? '-' : ''}${year}${month}${day}-1`
    default:
      return `${form.receiptNumberPrefix ? '-' : ''}1`
  }
}




const checkForChanges = () => {
  if (!originalData.value) return false
  if (form.name !== originalData.value.name ||
      form.description !== originalData.value.description ||
      form.receiptNumberPrefix !== originalData.value.receiptNumberPrefix ||
      form.receiptNumberFormat !== originalData.value.receiptNumberFormat) {
    return true
  }
  
  return false
}


const confirmBeforeLeaving = async () => {
  if (!hasUnsavedChanges.value) {
    return true
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    await submitForm()
    return true
  } else if (result.isDenied) {
    // Verwerfe die Änderungen
    hasUnsavedChanges.value = false
    return true
  } else {
    // Benutzer hat abgebrochen
    return false
  }
}


const handleCancel = async () => {
  const canLeave = await confirmBeforeLeaving()
  if (canLeave) {
    emit('cancel')
  }
}

const submitForm = async () => {
  if (!props.team) return

  errors.name = ''

  let isValid = true

  if (!form.name.trim()) {
    errors.name = 'Bereichsname ist erforderlich'
    isValid = false
  }

  if (!isValid) return

  try {
    const updatedTeam = await teamService.updateTeam(props.team.id, {
      name: form.name.trim(),
      description: form.description.trim(),
      receiptNumberPrefix: form.receiptNumberPrefix.trim(),
      receiptNumberFormat: form.receiptNumberFormat,
    })

    showSuccess('Bereich wurde aktualisiert')
    hasUnsavedChanges.value = false
    emit('success', updatedTeam)
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Bereichs:', err)
    emit('error', 'Bereich konnte nicht aktualisiert werden')
  }
}


watch([() => form.name, () => form.description, () => form.receiptNumberPrefix, () => form.receiptNumberFormat], () => {
  hasUnsavedChanges.value = checkForChanges()
}, { deep: true })


const handleBeforeUnload = (e) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}


onMounted(() => {
  initForm()
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Router-Guard für Navigation
onBeforeRouteLeave(async (to, from, next) => {
  if (!hasUnsavedChanges.value) {
    next()
    return
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    try {
      await submitForm()
      next()
    } catch (error) {
      console.error('Fehler beim Speichern:', error)
      next(false) // Verhindere Navigation bei Fehler
    }
  } else if (result.isDenied) {
    // Verwerfe die Änderungen und erlaube Navigation
    hasUnsavedChanges.value = false
    next()
  } else {
    // Benutzer hat abgebrochen - verhindere Navigation
    next(false)
  }
})

watch(() => props.team, () => {
  initForm()
}, { deep: true })
</script>
